import { useCallback, useMemo, useState } from 'react';
import { DropdownOption } from '../stores/types/pm.interface';
import { ComputedPromotionStatus } from '../stores/types/promotions.interface';

export interface PromotionStatusOption extends DropdownOption {
  id: ComputedPromotionStatus;
}

export const usePromotion = (companyId?: string) => {
  // -----------------------
  // FILTER STATE
  // -----------------------
  const [searches, setSearches] = useState<string[]>([]);
  const [status, setStatus] = useState<ComputedPromotionStatus[]>([]);
  const [page, setPage] = useState(1);

  // -----------------------
  // SEARCH (OR logic)
  // -----------------------
  const addSearch = useCallback((value: string) => {
    setSearches((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setPage(1);
  }, []);

  const removeSearch = useCallback((value: string) => {
    setSearches((prev) => prev.filter((v) => v !== value));
    setPage(1);
  }, []);

  // -----------------------
  // STATUS (multi-select OR)
  // -----------------------
  const toggleStatus = useCallback((value: ComputedPromotionStatus) => {
    setStatus((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setPage(1);
  }, []);

  const setStatuses = useCallback((statuses: ComputedPromotionStatus[]) => {
    setStatus(statuses);
    setPage(1);
  }, []);

  // -----------------------
  // CLEAR
  // -----------------------
  const clearAll = useCallback(() => {
    setSearches([]);
    setStatus([]);
    setPage(1);
  }, []);

  const clearSearch = useCallback(() => {
    setSearches([]);
    setPage(1);
  }, []);

  const clearStatus = useCallback(() => {
    setStatus([]);
    setPage(1);
  }, []);

  // -----------------------
  // DERIVED
  // -----------------------
  const numOfPills = useMemo(() => {
    return searches.length + status.length;
  }, [searches, status]);

  // -----------------------
  // API QUERY (LIKE CHARGERS)
  // -----------------------
  const queryArgs = useMemo(() => {
    if (!companyId) return undefined;

    return {
      companyId,
      limit: 1000,
      offset: 0,
    };
  }, [companyId]);

  return {
    // state
    searches,
    status,
    page,

    // actions
    addSearch,
    removeSearch,
    setStatuses,
    toggleStatus,

    setPage,

    clearAll,
    clearSearch,
    clearStatus,

    // derived
    numOfPills,

    // api
    queryArgs,
  };
};
